import React, { useEffect, useState } from 'react';
import { VStack, Text } from 'native-base';
import { textStyle } from 'src/styles/textStyle';
import { getText } from 'src/utils/i18n';
import { useDispatch, useSelector } from 'react-redux';
import * as AppActions from '../../redux/actions/appAction';
import { IStore } from 'src/redux/reducers';
import BaseDatePicker from '../BaseDatePicker';
import { createInclusiveLocalDateRange } from 'src/utils/dateRangeUtils';

interface IDateRangePickerProps {
  onFromDateChange?: (date: Date | null) => void;
  onToDateChange?: (date: Date | null) => void;
  fromDate?: Date;
  toDate?: Date;
  useRedux?: boolean;
}

export default function DateRangePicker(props: IDateRangePickerProps) {
  const { onFromDateChange, onToDateChange, fromDate, toDate, useRedux = false } = props;
  const dispatch = useDispatch();
  const dateRange = useSelector((store: IStore) => store.app.dateRange);

  const [internalFromDate, setInternalFromDate] = useState<Date | null>(() => {
    if (useRedux && dateRange?.from) {
      return new Date(dateRange.from * 1000);
    }
    return fromDate || null;
  });

  const [internalToDate, setInternalToDate] = useState<Date | null>(() => {
    if (useRedux && dateRange?.to) {
      return new Date(dateRange.to * 1000);
    }
    return toDate || null;
  });

  useEffect(() => {
    if (useRedux) {
      setInternalFromDate(
        dateRange?.from !== undefined ? new Date(dateRange.from * 1000) : null
      );
      setInternalToDate(
        dateRange?.to !== undefined ? new Date(dateRange.to * 1000) : null
      );
      return;
    }

    setInternalFromDate(fromDate || null);
    setInternalToDate(toDate || null);
  }, [dateRange?.from, dateRange?.to, fromDate, toDate, useRedux]);

  const updateReduxDateRange = (nextFromDate: Date | null, nextToDate: Date | null) => {
    if (!useRedux) {
      return;
    }

    dispatch(AppActions.setDateRange(
      nextFromDate && nextToDate
        ? createInclusiveLocalDateRange(nextFromDate, nextToDate)
        : undefined
    ));
  };

  const handleFromDateChange = (date: Date | null) => {
    setInternalFromDate(date);

    if (onFromDateChange) {
      onFromDateChange(date);
    }

    updateReduxDateRange(date, internalToDate);
  };

  const handleToDateChange = (date: Date | null) => {
    setInternalToDate(date);

    if (onToDateChange) {
      onToDateChange(date);
    }

    updateReduxDateRange(internalFromDate, date);
  };

  return (
    <VStack space={4}>
      <Text style={textStyle.subSectionText}>{getText('chooseDateRange')}</Text>
      <BaseDatePicker
        label={getText('from') || 'From'}
        value={internalFromDate || undefined}
        onChange={handleFromDateChange}
        maxDate={internalToDate || undefined}
      />
      <BaseDatePicker
        label={getText('to') || 'To'}
        value={internalToDate || undefined}
        onChange={handleToDateChange}
        minDate={internalFromDate || undefined}
      />
    </VStack>
  );
}
